/**
 * Today's decisions, as plain functions (tested in today-model.test.ts): which level
 * opens, what the note line says and what the pill does. No React, no storage.
 */
import { formatDuration } from '@arrow/game-engine';
import { DEFAULT_LEVEL, type Level, LEVELS } from '@arrow/puzzle-schema';

import type { ResumeInfo } from '../storage/persistence.ts';

export const LEVEL_NAME: Record<Level, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

/**
 * The level Today opens on: the one you last played, else one already started today,
 * else Easy on a first visit, else medium; always one the day actually has. When the
 * level you last played is solved today and another is still open, a fresh visit opens
 * on that one (`nextLevel`), so the big button looks forward. Coming straight back from
 * the solved level (`justLeft`) keeps it, with its result a tap away.
 */
export function initialLevel(
	available: readonly Level[],
	opts: {
		remembered: Level | null;
		firstVisit: boolean;
		started?: readonly Level[];
		/** Today's levels and whether each is solved. */
		levels?: readonly { level: Level; done: boolean }[];
		/** The level of today's daily the player has just come back from, in this visit. */
		justLeft?: Level | null;
	},
): Level {
	const has = (l: Level | null | undefined): l is Level => !!l && available.includes(l);
	if (has(opts.remembered)) {
		const solved = opts.levels?.some((l) => l.level === opts.remembered && l.done) ?? false;
		const next = solved && opts.justLeft !== opts.remembered && opts.levels ? nextLevel(opts.remembered, opts.levels) : null;
		return next && has(next) ? next : opts.remembered;
	}
	const started = opts.started?.find(has);
	if (started) return started;
	if (opts.firstVisit && has('easy')) return 'easy';
	if (has(DEFAULT_LEVEL)) return DEFAULT_LEVEL;
	return available[0] ?? DEFAULT_LEVEL;
}

export type Note =
	| { kind: 'first'; text: string; quiet: string }
	| { kind: 'started'; text: string; quiet: string }
	| { kind: 'solved'; text: string; quiet: string; next?: Level | undefined }
	/** Opened on the next level after solving `done`: its time, with its result a tap away. */
	| { kind: 'after'; text: string; quiet: string; done: Level }
	/** Nothing to add: the chosen forme above already says which level it is (QA1-6). */
	| { kind: 'none'; text: string; quiet: string };

/** The one line under the formes. */
export function noteFor(
	level: Level,
	opts: {
		resume: ResumeInfo;
		firstVisit: boolean;
		remembered: Level | null;
		/** Today's other levels and whether each is done, for "Try Hard next?". */
		levels: readonly { level: Level; done: boolean }[];
		/** Time to the next daily ("3h 12m"), for when the day's all done. */
		untilNext?: string | null;
		/** Today opened on this level because `after` (your level) is solved: its time. */
		after?: { level: Level; elapsedMs: number } | null;
	},
): Note {
	const { resume } = opts;
	if (resume.status === 'started') {
		const time = formatDuration(resume.elapsedMs);
		const count =
			resume.answersTotal !== null
				? `${resume.answersSolved} of ${resume.answersTotal} answers`
				: `${resume.answersSolved} ${resume.answersSolved === 1 ? 'answer' : 'answers'}`;
		return { kind: 'started', text: count, quiet: ` · ${time} in` };
	}
	if (resume.status === 'completed') {
		const next = nextLevel(level, opts.levels);
		const text = `Solved in ${formatDuration(resume.elapsedMs)}`;
		// A level left: the suggestion is the way to it (Today makes it a button that selects it).
		if (next) return { kind: 'solved', text, quiet: ` · Try ${LEVEL_NAME[next]} next?`, next };
		return { kind: 'solved', text, quiet: opts.untilNext ? ` · Next Sparrow in ${opts.untilNext}` : ' · More tomorrow' };
	}
	if (opts.firstVisit) {
		return level === 'easy'
			? { kind: 'first', text: 'New to Arrowwords?', quiet: ' Start with Easy.' }
			: { kind: 'first', text: 'New to Arrowwords?', quiet: opts.levels.some((l) => l.level === 'easy') ? ' Easy is a gentler start.' : ' How to play is in ≡.' };
	}
	if (opts.after) {
		return { kind: 'after', text: `${LEVEL_NAME[opts.after.level]} solved in ${formatDuration(opts.after.elapsedMs)}`, quiet: ' · See result', done: opts.after.level };
	}
	return { kind: 'none', text: '', quiet: '' };
}

/** The level to suggest after solving `level`: the next harder one not done, else an easier one. */
export function nextLevel(level: Level, levels: readonly { level: Level; done: boolean }[]): Level | null {
	const i = LEVELS.indexOf(level);
	const open = levels.filter((l) => !l.done && l.level !== level).map((l) => l.level);
	return open.find((l) => LEVELS.indexOf(l) > i) ?? [...open].reverse().find((l) => LEVELS.indexOf(l) < i) ?? null;
}

/** Every one of today's levels is solved. */
export function allDone(levels: readonly { done: boolean }[]): boolean {
	return levels.length > 0 && levels.every((l) => l.done);
}

/**
 * The time to the next daily, local midnight, in hours and minutes ("3h 12m", "5m"): minutes
 * are enough for "come back later", and the units keep it from reading as a clock time.
 * Rounded up, so it never says the next one's out early.
 */
export function untilMidnight(now: Date): string {
	const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
	const mins = Math.max(1, Math.ceil((midnight.getTime() - now.getTime()) / 60_000));
	const h = Math.floor(mins / 60);
	const m = mins % 60;
	return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export type PillAction = { kind: 'play' } | { kind: 'continue'; time: string } | { kind: 'result' };

export function pillFor(resume: ResumeInfo): PillAction {
	if (resume.status === 'completed') return { kind: 'result' };
	if (resume.status === 'started') return { kind: 'continue', time: formatDuration(resume.elapsedMs) };
	return { kind: 'play' };
}
